import { useMemo } from "react";
import type { NearbyResult } from "../../hooks/useNearbyEntities";
import type { Entity, EventOption } from "../../types";
import { getEventOptionsForEntity } from "../../config/defaultEventOptions";
import { useTypeSettings } from "../../contexts/TypeSettingsContext";
import { nextQuickActions } from "../../utils/eventOptionSelection";
import { formatDistance } from "../../utils/locationSharing";
import { entityTypeLabel } from "../../utils/tripDisplay";
import { TypeIcon } from "../common/TypeIcon";

interface LogBestGuessProps {
  guess: NearbyResult<Entity & { id: string }>;
  /** Log it now, with this as its first step. */
  onLog: (first: EventOption) => void;
  /** Open it the usual way, to pick a different first step. */
  onOpen: () => void;
  disabled?: boolean;
}

/**
 * "You're at Hagrid's — [Entered Line]": the place the sheet thinks you're at,
 * with its first step as one button. A tap logs straight away; the toast
 * that follows offers Undo.
 */
export function LogBestGuess({ guess, onLog, onOpen, disabled }: LogBestGuessProps) {
  const { entity, distanceMeters } = guess;
  const typeSettings = useTypeSettings(entity.type);
  const first = useMemo(() => {
    const options = getEventOptionsForEntity(entity.eventOptions, entity.type, typeSettings?.eventOptions);
    return nextQuickActions(options, []).options[0] ?? null;
  }, [entity.eventOptions, entity.type, typeSettings]);

  return (
    <section className="log-best-guess" aria-label="Best guess">
      <button type="button" className="log-best-guess-place" onClick={onOpen} disabled={disabled}>
        <span className="log-best-guess-icon">
          {entity.imageUrl ? <img src={entity.imageUrl} alt="" /> : <TypeIcon type={entity.type} name={entity.displayName} tile />}
        </span>
        <span className="log-best-guess-text">
          <small>You're at</small>
          <b>{entity.displayName}</b>
          <small>
            {entityTypeLabel(entity.type)} · {formatDistance(distanceMeters)}
          </small>
        </span>
      </button>
      <button
        type="button"
        className="log-best-guess-go"
        onClick={() => (first ? onLog(first) : onOpen())}
        disabled={disabled}
      >
        {first ? first.displayName : "Log"}
      </button>
    </section>
  );
}
